<template>
  <div class="service-system-form">
    <!-- Service History Section -->
    <section class="service-history mt-0">
      <div class="d-flex justify-content-between align-items-center">
        <p class="section-label mb-0">
          {{ $t('service.service_history') }}
        </p>
        <FeatherIcon
          v-if="$can('manage', 'create-service')"
          v-b-modal.create-service-form
          class="clickable-icon"
          icon="PlusIcon"
          size="24"
        />
        <FeatherIcon
          v-else
          v-b-tooltip.hover
          :title="$t('general.create_service_disabled')"
          class="disabled-icon"
          icon="PlusIcon"
          size="24"
        />
      </div>
    </section>

    <!-- Create Service Modal -->
    <b-modal
      id="create-service-form"
      ref="modal"
      centered
      size="lg"
      :title="$t('service.create_new_service')"
      hide-footer
      @hidden="onHidden"
    >
      <validation-observer
        ref="formValidation"
        #default="{ invalid }"
      >
        <!-- Basic Information Section -->
        <div class="basic-info mb-2">
          <h6 class="mb-1">
            {{ $t('service.basic_info') }}
          </h6>
          <div class="info-card border rounded p-2">
            <div class="d-flex align-items-center">
              <!-- Profile Image -->
              <div class="profile-image-container mr-3">
                <img
                  v-if="visitorMetaData.profileUrl"
                  :src="visitorMetaData.profileUrl"
                  :alt="visitorMetaData.name"
                  class="profile-image"
                >
                <div
                  v-else
                  class="profile-image-placeholder"
                >
                  <FeatherIcon
                    icon="UserIcon"
                    size="48"
                  />
                </div>
              </div>

              <!-- Info Details -->
              <div class="flex-grow-1">
                <div class="h-stack">
                  <div class="info-group">
                    <label class="d-block text-muted small">{{
                      $t('field.name')
                    }}</label>
                    <span class="font-weight-medium">{{
                      visitorMetaData.name || '-'
                    }}</span>
                  </div>
                  <div class="info-group">
                    <label class="d-block text-muted small">{{
                      $t('field.enterprise')
                    }}</label>
                    <span class="font-weight-medium">{{
                      enterpriseName || '-'
                    }}</span>
                  </div>
                </div>
                <div class="h-stack mt-2">
                  <div class="info-group">
                    <label class="d-block text-muted small">{{
                      $t('general.building_type')
                    }}</label>
                    <span class="font-weight-medium">{{
                      buildingType || '-'
                    }}</span>
                  </div>
                  <div class="info-group">
                    <label class="d-block text-muted small">{{
                      $t('general.building_name')
                    }}</label>
                    <span class="font-weight-medium">{{
                      buildingName || '-'
                    }}</span>
                  </div>
                </div>
                <div class="h-stack mt-2">
                  <div class="info-group">
                    <label class="d-block text-muted small">{{
                      $t('field.room')
                    }}</label>
                    <span class="font-weight-medium">{{
                      roomName || '-'
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Service Details Section -->
        <div class="service-details mb-2">
          <h6 class="mb-1">
            {{ $t('service.details') }}
          </h6>
          <div class="details-card border rounded p-2">
            <!-- Service Name with Language Selection -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.serviceName')"
              :rules="
                locale === $i18n.locale.split('-')[0]
                  ? 'required|min:3|max:47'
                  : 'min:3|max:47'
              "
            >
              <b-form-group
                :label="$t('field.serviceName')"
                :state="errors.length === 0 ? null : false"
                class="mb-2"
              >
                <div class="d-flex align-items-center">
                  <div class="flex-grow-1 mr-1">
                    <b-form-input
                      v-model="formData.serviceName[locale]"
                      :placeholder="$t('placeholder.serviceName')"
                      :state="errors.length === 0 ? null : false"
                    />
                  </div>
                  <div style="min-width: 80px">
                    <b-form-select
                      v-model="locale"
                      :options="locales"
                      class="language-select"
                    />
                  </div>
                </div>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Project Selection -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('breadcrumb.project')"
              rules="required"
            >
              <b-form-group
                :label="$t('breadcrumb.project')"
                :state="errors.length === 0 ? null : false"
                class="mb-2"
              >
                <n-async-single-select
                  v-model="formData.project"
                  repository="project"
                  selection-key="id"
                  selection-label="translations"
                  :is-object="true"
                  :query="{ isActive: true }"
                  :state="errors.length === 0 ? null : false"
                  :placeholder="$t('placeholder.selectProject')"
                  v-bind:tmpList.sync="projectList"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <div class="service-type-grid">
              <!-- Category Selection -->
              <validation-provider
                v-slot="{ errors }"
                :name="$t('field.category')"
                rules="required"
              >
                <b-form-group
                  :label="$t('field.category')"
                  :state="errors.length === 0 ? null : false"
                  class="mb-2"
                >
                  <n-async-single-select
                    v-model="formData.category"
                    repository="serviceCategory"
                    selection-key="code"
                    selection-label="translations"
                    :reduce="true"
                    :must-excluded-ids="[]"
                    :state="errors.length === 0 ? null : false"
                    :placeholder="$t('placeholder.selectCategory')"
                    :is-object="true"
                    @change="handleCategoryChange"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <!-- Type Selection -->
            </div>
            <div class="service-type-grid">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('field.type')"
                rules="required"
              >
                <b-form-group
                  :label="$t('field.type')"
                  :state="errors.length === 0 ? null : false"
                  class="mb-2"
                >
                  <n-async-single-select
                    v-model="formData.type"
                    repository="serviceType"
                    selection-key="code"
                    selection-label="translations"
                    :readonly="!formData.category"
                    :query="{
                      categoryCode: formData.category
                        ? formData.category.code
                        : null
                    }"
                    :reduce="true"
                    :state="errors.length === 0"
                    :placeholder="$t('placeholder.selectType')"
                    :is-object="true"
                    @change="handleTypeChange"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                :name="$t('field.subType')"
              >
                <b-form-group
                  :label="$t('field.subType')"
                  :state="errors.length === 0 ? null : false"
                  class="mb-2"
                >
                  <n-async-multi-select
                    :key="resetSubIndex"
                    v-model="formData.subTypes"
                    repository="serviceSubType"
                    selection-key="code"
                    selection-label="translations"
                    clearable
                    :placeholder="$t('placeholder.selectSubType')"
                    :state="errors.length > 0 ? 'is-invalid' : null"
                    :readonly="!formData.type"
                    :is-object="true"
                    always-fresh
                    :immediate="false"
                    :query="{
                      serviceTypeCodes: formData.type
                        ? formData.type.code
                        : null,
                      serviceCategoryCodes: formData.category
                        ? formData.category.code
                        : null,
                    }"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>

            <!-- Dynamic Fields Based on Service Type -->
            <div
              v-if="isDynamicFields"
              class="dynamic-fields pt-2 mt-2 border-top"
            >
              <water-delivery-form
                v-if="formData.type.code === 'WATER_DELIVERY_ORDER'"
                v-model="formData.dynamicFields"
              />
              <cleaning-form
                v-else-if="formData.type.code === 'CLEANING_ORDER'"
                v-model="formData.dynamicFields"
              />
              <laundry-form
                v-else-if="formData.type.code === 'LAUNDRY_ORDER'"
                v-model="formData.dynamicFields"
                :chat-channel-id="chatChannelId"
              />
              <maintenance-form
                v-else-if="formData.category.code === 'MAINTENANCE'"
                v-model="formData.dynamicFields"
                :chat-channel-id="chatChannelId"
              />
            </div>
          </div>
        </div>

        <b-row class="mt-3">
          <b-col class="text-right">
            <n-button-loading
              variant="primary"
              :loading="submitting"
              :disabled="invalid || submitting || !formData.serviceName[$i18n.locale.split('-')[0]]"
              @submit="onSubmit"
            >
              {{ $t('button.save') }}
            </n-button-loading>
          </b-col>
        </b-row>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BFormSelect,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import NAsyncMultiSelect from '@/components/NAsyncMultiSelect'
import RepositoryFactory from '@/repositories/RepositoryFactory'
import NButtonLoading from '@/components/NButtonLoading.vue'
import WaterDeliveryForm from '@/components/WateDeliveryForm.vue'
import CleaningForm from '@/components/CleaningForm.vue'
import LaundryForm from '@/components/LaundryForm.vue'
import MaintenanceForm from '@/components/MaintenanceForm.vue'
import ToastificationAvatar from '@/@core/components/toastification/ToastificationAvatar.vue'

const ServiceSystemRepository = RepositoryFactory.get('serviceSystem')

export default {
  name: 'ServiceSystemForm',
  components: {
    BModal,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BFormSelect,
    ValidationProvider,
    ValidationObserver,
    NAsyncSingleSelect,
    NAsyncMultiSelect,
    NButtonLoading,
    WaterDeliveryForm,
    CleaningForm,
    LaundryForm,
    MaintenanceForm,
  },
  props: {
    visitorMetaData: {
      type: Object,
      required: true,
    },
    chatChannelId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      submitting: false,
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
      formData: {
        serviceName: {
          en: '',
          zh: '',
          vi: '',
          km: '',
        },
        projectId: null,
        project: null,
        category: null,
        type: null,
        subTypes: [],
        dynamicFields: {},
      },
      categoryOptions: [],
      typeOptions: [],
      subTypeOptions: [],
      locale: this.$i18n.locale.split('-')[0],
      resetSubIndex: 0,
      projectList: [],
    }
  },
  computed: {
    enterpriseName() {
      if (typeof this.visitorMetaData?.enterpriseName === 'object') {
        return this.visitorMetaData?.enterpriseName?.[this.locale] || this.visitorMetaData?.enterpriseName?.en || this.visitorMetaData?.enterpriseName?.zh || this.visitorMetaData?.enterpriseName?.km || this.visitorMetaData?.enterpriseName?.vi || this.visitorMetaData?.enterpriseName
      }
      return this.visitorMetaData?.enterpriseName
    },
    buildingName() {
      if (typeof this.visitorMetaData?.buildingName === 'object') {
        return this.visitorMetaData?.buildingName?.[this.locale] || this.visitorMetaData?.buildingName?.en || this.visitorMetaData?.buildingName?.zh || this.visitorMetaData?.buildingName?.km || this.visitorMetaData?.buildingName?.vi || this.visitorMetaData?.buildingName
      }
      return this.visitorMetaData?.buildingName
    },
    buildingType() {
      if (typeof this.visitorMetaData?.buildingType === 'object') {
        return this.visitorMetaData?.buildingType?.[this.locale] || this.visitorMetaData?.buildingType?.en || this.visitorMetaData?.buildingType?.zh || this.visitorMetaData?.buildingType?.km || this.visitorMetaData?.buildingType?.vi || this.visitorMetaData?.buildingType
      }
      return this.visitorMetaData?.buildingType
    },
    roomName() {
      if (typeof this.visitorMetaData?.roomName === 'object') {
        return this.visitorMetaData?.roomName?.[this.locale] || this.visitorMetaData?.roomName?.en || this.visitorMetaData?.roomName?.zh || this.visitorMetaData?.roomName?.km || this.visitorMetaData?.roomName?.vi || this.visitorMetaData?.roomName
      }
      return this.visitorMetaData?.roomName
    },
    priceTypeOptions() {
      return [
        { value: 1, text: this.$t('field.fixed') },
        { value: 2, text: this.$t('field.range') },
        { value: 3, text: this.$t('field.negotiate') },
      ]
    },
    // Dynamic Fields Configuration
    dynamicFields() {
      const typeConfigs = {}
      return typeConfigs[this.formData.type?.code] || []
    },
    isDynamicFields() {
      return (
        this.formData.type
        && this.formData.type?.code
        && (this.formData.type?.code === 'WATER_DELIVERY_ORDER'
          || this.formData.type?.code === 'CLEANING_ORDER'
          || this.formData.type?.code === 'LAUNDRY_ORDER'
          || this.formData.category?.code === 'MAINTENANCE')
      )
    },
  },
  watch: {
    'formData.type': {
      handler() {
        // Reset dynamic fields when type changes
        this.formData.dynamicFields = {}
        // Initialize default values for the new type
        if (this.dynamicFields.length) {
          this.dynamicFields.forEach(field => {
            this.formData.dynamicFields[field.name] = field.type === 'number' ? null : ''
          })
        }
      },
      immediate: true,
    },
    '$i18n.locale': function (val) {
      const [locale] = val.split('-')
      this.locale = locale
    },
  },
  mounted() {
    this.fetchOptions()
  },
  methods: {
    handleTypeChange() {
      this.resetSubIndex += 1
    },
    show() {
      this.$refs.modal.show()
    },
    hide() {
      this.$refs.modal.hide()
    },
    onHidden() {
      this.resetForm()
      this.$emit('hide')
    },
    resetForm() {
      this.formData = {
        serviceName: {
          en: '',
          zh: '',
          vi: '',
          km: '',
        },
        projectId: null,
        project: null,
        category: null,
        type: null,
        dynamicFields: {},
      }
      if (this.$refs.formValidation) {
        this.$refs.formValidation.reset()
      }
    },
    async fetchOptions() {
      try {
        const [categoryResponse, typeResponse] = await Promise.all([
          ServiceSystemRepository.getServiceCategory(),
          ServiceSystemRepository.getServiceType(),
        ])

        if (categoryResponse.data.data.list) {
          this.categoryOptions = categoryResponse.data.data.list.map(
            category => ({
              value: category.value || category.id,
              text: category.text || category.name,
            }),
          )
        }

        if (typeResponse.data.data.list) {
          this.typeOptions = typeResponse.data.data.list.map(type => ({
            value: type.value || type.id,
            text: type.text || type.name,
          }))
        }
      } catch (error) {
        console.error('Error fetching options:', error)
      }
    },
    resolveAddressValue(raw) {
      if (!raw) return null
      if (typeof raw === 'string') return raw.trim()
      if (typeof raw === 'object') {
        const isCustom = raw.isCustom || raw.typeKey === 'other'
        const val = isCustom ? (raw.customAddress || raw.addressName || '') : (raw.addressName || raw.name || '')
        return (val || '').toString().trim()
      }
      return null
    },
    buildParamsFromDynamicFields() {
      const params = []
      const dynamicFields = this.formData.dynamicFields || {}
      
      // Helper function to create multilingual name object
      const createMultilingualName = (enText, zhText, kmText, viText) => ({
        en: enText,
        zh: zhText || enText,
        km: kmText || enText,
        vi: viText || enText,
      })
      
      // Process each field in dynamicFields
      Object.keys(dynamicFields).forEach(key => {
        const value = dynamicFields[key]
        
        // Skip null, undefined, empty strings, and the address field (handled separately)
        if (value == null || value === '' || key === 'address') return
        
        let paramName = {}
        let paramValue = value
        let paramType = 'text'
        
        // Map field keys to proper names with translations
        switch (key) {
          case 'orderId':
            paramName = createMultilingualName('Order ID', '订单编号', 'លេខសម្គាល់ការបញ្ជាទិញ', 'Mã đơn hàng')
            break
          case 'createdBy':
            paramName = createMultilingualName('Created By', '创建者', 'បង្កើតដោយ', 'Được tạo bởi')
            break
          case 'description':
            paramName = createMultilingualName('Description', '描述', 'ការពិពណ៌នា', 'Sự miêu tả')
            break
          case 'expectedDate':
            paramName = createMultilingualName('Expected Date', '预计日期', 'កាលបរិច្ឆេទរំពឹងទុក', 'Ngày dự kiến')
            // Handle special case for ASAP
            if (value === 'ASAP' || (typeof value === 'object' && value.en === 'ASAP')) {
              paramValue = createMultilingualName('ASAP', '尽快', 'ឱ្យបានឆាប់', 'càng sớm càng tốt')
            }
            break
          case 'files':
            paramName = createMultilingualName('Files', '文件', 'ឯកសារ', 'Tập tin')
            paramType = 'file'
            paramValue = ''
            break
          case 'quantity':
            paramName = createMultilingualName('Quantity', '数量', 'បរិមាណ', 'Số lượng')
            break
          case 'price':
            paramName = createMultilingualName('Price', '价格', 'តម្លៃ', 'Giá cả')
            break
          case 'notes':
            paramName = createMultilingualName('Notes', '备注', 'កំណត់ចំណាំ', 'Ghi chú')
            break
          case 'priority':
            paramName = createMultilingualName('Priority', '优先级', 'អាទិភាព', 'Ưu tiên')
            break
          case 'serviceDate':
            paramName = createMultilingualName('Service Date', '服务日期', 'កាលបរិច្ឆេទសេវាកម្ម', 'Ngày dịch vụ')
            break
          case 'serviceTime':
            paramName = createMultilingualName('Service Time', '服务时间', 'ពេលវេលាសេវាកម្ម', 'Thời gian dịch vụ')
            break
          case 'specialInstructions':
            paramName = createMultilingualName('Special Instructions', '特殊说明', 'ការណែនាំពិសេស', 'Hướng dẫn đặc biệt')
            break
          case 'scheduleType':
            paramName = createMultilingualName('Schedule Type', '计划类型', 'ប្រភេទកាលវិភាគ', 'Loại lịch trình')
            break
          case 'scheduleDate':
            paramName = createMultilingualName('Schedule Date', '计划日期', 'កាលបរិច្ឆេទកាលវិភាគ', 'Ngày lịch trình')
            break
          case 'chargeType':
            paramName = createMultilingualName('Charge Type', '收费类型', 'ប្រភេទការគិតថ្លៃ', 'Loại phí')
            break
          case 'items':
            paramName = createMultilingualName('Items', '订单', 'ការបញ្ជាទិញ', 'Đơn hàng')
            break
          default:
            // For unknown fields, use the key as the English name and create basic translations
            const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
            paramName = createMultilingualName(capitalizedKey, capitalizedKey, capitalizedKey, capitalizedKey)
        }
        
        const param = {
          type: paramType,
          name: paramName,
          key,
          value: paramValue,
        }
        
        // Add files array for file type
        if (paramType === 'file' && Array.isArray(value)) {
          param.files = value.map(file => ({
            mimeType: file.mimeType || 'application/octet-stream',
            url: file.url || file.src || file.path || '',
          }))
        }
        
        params.push(param)
      })
      
      // Add address as a separate parameter if it exists
      const addressValue = this.resolveAddressValue(dynamicFields.address)
      if (addressValue) {
        params.push({
          type: 'text',
          name: createMultilingualName('Address', '地址', 'អាស័យដ្ឋាន', 'Địa chỉ'),
          key: 'address',
          value: addressValue,
        })
      }
      
      return params
    },
    async onSubmit() {
      try {
        const isValid = await this.$refs.formValidation.validate()
        if (!isValid || this.submitting) return

        this.submitting = true
        const selectedProject = this.projectList.find(project => project.id === this.formData.project?.id)

        // Map the form data to the required payload structure
        const payload = {
          chatChannelId: this.chatChannelId,
          projectId: this.formData.project?.id || this.formData.project?.projectId || this.formData.projectId,
          projectName: selectedProject && Object.keys(selectedProject.translationsObj)?.length ? selectedProject.translationsObj : this.formData.project?.name,
          serviceCategory: {
            id: this.formData.category?.id,
            name: this.formData.category?.name,
            code: this.formData.category?.code,
          },
          serviceType: {
            name: this.formData.type?.name,
            code: this.formData.type?.code,
          },
          serviceSubTypes: this.formData.subTypes?.map(subType => ({
            id: subType?.id || undefined,
            name: subType?.name || undefined,
            code: subType?.code || undefined,
          })),
          serviceName: this.formData.serviceName,
          finalResultParams: this.formData.dynamicFields,
          meta: {
            ...this.visitorMetaData,
          },
          params: this.buildParamsFromDynamicFields(),
        }

        await ServiceSystemRepository.createService(payload)
        this.$toast({
          component: ToastificationAvatar,
          position: 'top-right',
          timeout: 10000,
          props: {
            title: this.$t('alert.service'),
            icon: 'CastIcon',
            variant: 'success',
            text: this.$t('service.create_success'),
          },
        })
        this.hide()
        this.resetForm()
        this.$emit('submit')
      } catch (error) {
        console.error('Error creating service:', error)
      } finally {
        this.submitting = false
      }
    },
    handleCategoryChange() {
      this.formData.type = null
      this.resetSubIndex += 1
    },
  },
}
</script>

<style lang="scss" scoped>
  .h-stack {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1em;
  }

  .service-type-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }

  .language-select {
    height: 100%;
  }

  .disabled-icon {
    cursor: not-allowed;
    opacity: 0.3;
  }

  .clickable-icon {
    cursor: pointer;
  }

  .service-details,
  .price-configuration,
  .basic-info {
    .details-card,
    .price-details,
    .info-card {
      border: 1px solid #dee2e6;
      border-radius: 0.25rem;
    }
  }

  .profile-image-container {
    width: 80px;
    height: 80px;
    flex-shrink: 0;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid #dee2e6;
    background-color: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .profile-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .info-group {
    label {
      margin-bottom: 0.25rem;
    }

    span {
      font-size: 0.95rem;
    }
  }
</style>
